/**
 * @render react
 * @name ReportCard
 * pros={[{title:'good something'}]} cons={[{title:'bad something'}]} />
 */

/* eslint-disable jsx-a11y/anchor-is-valid */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Price } from 'Components/atoms/Price';
import { Link } from 'Components/molecules/Link';
import { Rating } from 'Components/molecules/Rating';
import { VerdictExtra } from 'Components/molecules/VerdictExtra';

import './_style.scss';

/**
 * ReportCard component
 *
 * @param {Object} props
 * @param {Object} props.extraComponent - Extra component to be prepended to the report card. Can be a PrimaryMedia one
 * @param {Object} props.priceOptions - Object that contains the props to pass to the Price component
 * @param {string} props.prosIcon - Icon for pros
 * @param {string} props.consIcon - Icon for cons
 * @param {number|bool} props.rating - Rating number
 * @param {Component} props.pros - sanitized text
 * @param {Component} props.cons - sanitized text
 * @param {Array} props.ctas - Array of objects with the following keys: title, url
 * @param {bool} props.showProsCons - boolean to decide if pros & cons are shown (default: true)
 * @param {string} props.priceCtaLabel - label for the price cta
 * @param {string} props.priceCtaUrl - href for the price cta
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {object} props.extraClassNames - Extra classes to be added to the component
 * @param {number} props.ratingsRounding - rounding for ratings
 * @param {Array} props.extras - extra meta information for the report card. Array items are objects containing a label and a value
 * @param {Object} props.context - Extra information coming from Polaris provider
 */

const ReportCard = ({
  cons,
  consIcon,
  consLabel,
  context: {
    cssPrefix,
    ratingFillColor,
    ratingFillInactiveColor,
    reportCardRatingConfig,
  },
  ctas,
  extraClassNames,
  extraComponent,
  extras,
  layout,
  loadStyles,
  priceCtaLabel,
  priceCtaUrl,
  priceCtaUrlRel,
  priceLabel,
  priceOptions,
  rrpSavingsPriceCtaLabel,
  rrpSavingsPriceCtaUrl,
  rrpSavingsPriceCtaUrlRel,
  rrpSavingsPriceLabel,
  rrpSavingsPriceOptions,
  pros,
  prosIcon,
  prosLabel,
  ratings,
  ratingsRounding,
  showProsCons,
  starFill,
  starFillInactive,
  starsDimensions,
  starsPath,
  starsSpacings,
  starsViewBox,
}) => {
  if (!pros && !cons && ratings.length === 0 && Object.keys(priceOptions).length === 0) {
    return false;
  }

  const priceBlock = (
    priceBlockLabel,
    priceBlockOptions,
    priceBlockCtaUrl,
    priceBlockCtaLabel,
    priceBlockCtaUrlRel,
  ) => (
    <div className={`${cssPrefix}__report-card--price-container ${cssPrefix}__report-card--section-container`}>
      {priceBlockLabel && (
        <div className={`${cssPrefix}__report-card--label`}>
          {priceBlockLabel}
        </div>
      )}
      <div className={`${cssPrefix}__report-card--price`}>
        <Price {...priceBlockOptions} />
      </div>
      {priceBlockCtaLabel && (
        <Link
          href={priceBlockCtaUrl}
          extraClassNames={{ [`${cssPrefix}__report-card--price-cta`]: true }}
          rel={priceBlockCtaUrlRel}
        >
          {priceBlockCtaLabel}
        </Link>
      )}
    </div>
  );

  const rrpPriceBlock = priceOptions
  && (priceOptions.min > 0 || priceOptions.max > 0)
  && priceBlock(
    priceLabel,
    priceOptions,
    priceCtaUrl,
    priceCtaLabel,
    priceCtaUrlRel,
  );

  const rrpSavingsPriceBlock = rrpSavingsPriceOptions
  && rrpSavingsPriceOptions.min > 0
  && priceBlock(
    rrpSavingsPriceLabel,
    rrpSavingsPriceOptions,
    rrpSavingsPriceCtaUrl,
    rrpSavingsPriceCtaLabel,
    rrpSavingsPriceCtaUrlRel,
  );

  const ctasBlock = ctas.length > 0 && (
    <div className={`${cssPrefix}__report-card--ctas`}>
      {ctas.map((cta, index) => (
        <Link
          extraClassNames={{
            [`${cssPrefix}__report-card--cta`]: true,
            ...cta?.extraClassNames,
          }}
          href={cta?.url}
          rel={cta?.urlRel || ''}
          key={index}
          onClick={cta?.onClick || undefined}
        >
          {cta?.title}
        </Link>
      ))}
    </div>
  );

  const toList = (list) => {
    if (list.length > 0) {
      return (
        <ul>
          {list.map((item, index) => (
            <li key={`report-list-item-${index}`}>{item}</li>
          ))}
        </ul>
      );
    }
    return null;
  };

  const formattedPros = Array.isArray(pros) ? toList(pros) : pros;
  const formattedCons = Array.isArray(cons) ? toList(cons) : cons;

  return (
    <div className={classNames({
      [`${cssPrefix}__report-card`]: loadStyles,
      ...extraClassNames,
    })}
    >
      {extraComponent && (
        <div className={`${cssPrefix}__report-card--component`}>
          {extraComponent}
        </div>
      )}
      <div className={classNames({
        [`${cssPrefix}__report-card--meta`]: loadStyles,
        // indicate that container is column based depending on its content
        '-has-rows': (ratings.length !== 0 || priceOptions) && (pros || cons),
        ...extraClassNames,
      })}
      >
        { /* display column if at least one value exists */ }
        {(ratings.length !== 0 || priceOptions || extras.length !== 0) && (
          <div className={`${cssPrefix}__report-card--row`}>
            <div className={`${cssPrefix}__report-card--ratings`}>
              {ratings.map((rating, i) => {
                if (rating.value !== undefined && rating.value > 0) {
                  return (
                    <div className={`${cssPrefix}__report-card--ratings-container ${layout === 'oneColumn' ? '-oneColumn' : ''}`} key={i}>
                      <Rating
                        key={i}
                        label={rating.label}
                        rating={rating.value}
                        showTextRating={rating.showTextRating || false}
                        showTextRatingWithTotal={rating.showTextRatingWithTotal}
                        numberOfStars={5}
                        fill={starFill || reportCardRatingConfig?.color?.ratingFillColor || ratingFillColor}
                        fillInactive={starFillInactive || reportCardRatingConfig?.color?.fillInactive || ratingFillInactiveColor}
                        iconPath={starsPath || reportCardRatingConfig?.icon?.iconPath}
                        iconViewBox={starsViewBox || reportCardRatingConfig?.icon?.iconViewBox}
                        widgetDimensions={starsDimensions || reportCardRatingConfig?.sizing?.dimensions}
                        widgetSpacings={starsSpacings || reportCardRatingConfig?.sizing?.spacings}
                        extraClassNames={{
                          [`${cssPrefix}__report-card--rating`]: true,
                          [`${cssPrefix}__report-card--section-container`]: true,
                        }}
                        extraContent={rating.extraContent}
                        rounding={ratingsRounding}
                      />
                      {rating.ctaLabel && (
                        <Link
                          href={rating.ctaUrl}
                          extraClassNames={{ [`${cssPrefix}__report-card--rating-cta`]: true }}
                          rel={rating.ctaUrlRel ? rating.ctaUrlRel : ''}
                        >
                          {rating.ctaLabel}
                        </Link>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>

            {rrpPriceBlock}
            {rrpSavingsPriceBlock}

            {extras.length !== 0 && (
              <div className={`${cssPrefix}__report-card--extras`}>
                {extras.map((extra, index) => (
                  <div className={`${cssPrefix}__report-card--extra ${cssPrefix}__report-card--section-container`} key={index}>
                    <div className={`${cssPrefix}__report-card--extra-label ${cssPrefix}__report-card--label`}>
                      {extra.label}
                    </div>
                    <div className={`${cssPrefix}__report-card--extra-value ${cssPrefix}__report-card--value`}>
                      {extra.value}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        { /* display column if at least one value exists */ }
        {((pros || cons) && showProsCons) && (
          <div className={`${cssPrefix}__report-card--row -proscons ${layout === 'oneColumn' ? '-oneColumn' : ''}`}>
            {pros && (
              <VerdictExtra
                label={prosLabel}
                icon={prosIcon}
                content={formattedPros}
                extraClassNames={{
                  [`${cssPrefix}__report-card--pros-container`]: true,
                  [`${cssPrefix}__report-card--section-container`]: true,
                }}
              />
            )}

            {cons && (
              <VerdictExtra
                label={consLabel}
                icon={consIcon}
                content={formattedCons}
                extraClassNames={{
                  [`${cssPrefix}__report-card--cons-container`]: true,
                  [`${cssPrefix}__report-card--section-container`]: true,
                }}
              />
            )}
          </div>
        )}

        {ctasBlock}
      </div>
    </div>
  );
};

ReportCard.propTypes = {
  cons: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.node,
    PropTypes.object,
  ]),
  consIcon: PropTypes.string,
  consLabel: PropTypes.string,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
    ratingFillColor: PropTypes.string,
    ratingFillInactiveColor: PropTypes.string,
    reportCardRatingConfig: PropTypes.shape({
      color: PropTypes.shape({
        ratingFillColor: PropTypes.string,
        fillInactive: PropTypes.string,
      }),
      icon: PropTypes.shape({
        iconPath: PropTypes.string,
        iconViewBox: PropTypes.string,
      }),
      sizing: PropTypes.shape({
        dimensions: PropTypes.string,
        spacings: PropTypes.string,
      }),
    }),
  }),
  ctas: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  extraClassNames: PropTypes.shape(),
  extraComponent: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  extras: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })),
  layout: PropTypes.oneOf(['default', 'oneColumn']),
  loadStyles: PropTypes.bool,
  priceCtaLabel: PropTypes.string,
  priceCtaUrl: PropTypes.string,
  priceCtaUrlRel: PropTypes.string,
  priceLabel: PropTypes.string,
  rrpSavingsPriceCtaLabel: PropTypes.string,
  rrpSavingsPriceCtaUrl: PropTypes.string,
  rrpSavingsPriceCtaUrlRel: PropTypes.string,
  rrpSavingsPriceLabel: PropTypes.string,
  priceOptions: PropTypes.shape(),
  rrpSavingsPriceOptions: PropTypes.shape(),
  pros: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.node,
    PropTypes.object,
  ]),
  prosIcon: PropTypes.string,
  prosLabel: PropTypes.string,
  ratings: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
    ]),
    extraContent: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.node,
      PropTypes.object,
    ]),
  })),
  ratingsRounding: PropTypes.number,
  showProsCons: PropTypes.bool,
  starFill: PropTypes.string,
  starFillInactive: PropTypes.string,
  starsDimensions: PropTypes.string,
  starsPath: PropTypes.string,
  starsSpacings: PropTypes.string,
  starsViewBox: PropTypes.string,
};

ReportCard.defaultProps = {
  cons: false,
  consIcon: null,
  consLabel: '➖',
  context: {},
  ctas: [],
  extraClassNames: {},
  extraComponent: null,
  extras: [],
  layout: 'default',
  loadStyles: true,
  priceCtaLabel: '',
  priceCtaUrl: '',
  priceCtaUrlRel: '',
  priceLabel: 'RRP',
  priceOptions: {},
  rrpSavingsPriceCtaLabel: '',
  rrpSavingsPriceCtaUrl: '',
  rrpSavingsPriceCtaUrlRel: '',
  rrpSavingsPriceLabel: 'Avg. savings',
  rrpSavingsPriceOptions: {},
  pros: false,
  prosIcon: null,
  prosLabel: '➕',
  ratings: [],
  ratingsRounding: 0,
  showProsCons: true,
  starFill: '',
  starFillInactive: '',
  starsDimensions: '',
  starsPath: '',
  starsSpacings: '',
  starsViewBox: '',
};

export default withPolaris(ReportCard);
